import { Button, Group, Paper, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { IconFileExport } from '@tabler/icons-react';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import ExportCsvButton from '@/components/common/ExportCsvButton';
import { toast } from '@/components/ui/providers/AlertProvider';
import { downloadCsv } from '../ExportCsv';
import type { Row } from '../types';

interface Props {
  rows: Row[];
  columns: TableColumn<Row>[];
  columnCount: number;
  filename: string;
  mode: 'paged' | 'streamed';
  /** Paged mode only: pulls every matching page and returns the full set (null on failure). */
  fetchAll?: () => Promise<Row[] | null>;
  totalCount: number;
  busy?: boolean;
  progress?: { loaded: number; total: number } | null;
  /** Request export after the missing-data run: the finished rows, exported as they are. */
  localRows?: Row[] | null;
  /** When set, Export is disabled and this explains why (e.g. "press Start first"). */
  blockedReason?: string | null;
}

export function ExportBar({
  rows, columns, columnCount, filename, mode, fetchAll, totalCount, busy = false, progress,
  localRows = null, blockedReason = null,
}: Props) {
  const handlePagedExport = async () => {
    if (localRows) {
      downloadCsv(localRows, columns, filename);
      return;
    }
    const all = await fetchAll?.();
    if (!all) {
      toast.error('Export failed', 'The records could not be loaded. Please try again.');
      return;
    }
    downloadCsv(all, columns, filename);
  };

  const recordCount = localRows ? localRows.length : totalCount;
  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.loaded / progress.total) * 100))
    : 0;
  const finished = Boolean(progress) && !busy;

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="center">
        <Text size="sm" c="dimmed">
          {columnCount} column{columnCount === 1 ? '' : 's'} ·{' '}
          {mode === 'paged'
            ? `${recordCount.toLocaleString()} record${recordCount === 1 ? '' : 's'} will be exported`
            : `${rows.length.toLocaleString()} record${rows.length === 1 ? '' : 's'} will be exported`}
        </Text>

        {mode === 'paged' ? (
          <Tooltip label={blockedReason} disabled={!blockedReason} withArrow>
            {/* A disabled button swallows hover events, so the tooltip hangs off a wrapper. */}
            <div>
              <Button
                variant="outline"
                color="green"
                leftSection={<IconFileExport size={16} />}
                loading={busy}
                disabled={columnCount === 0 || recordCount === 0 || Boolean(blockedReason)}
                onClick={handlePagedExport}
              >
                Export CSV
              </Button>
            </div>
          </Tooltip>
        ) : (
          <Tooltip label={blockedReason} disabled={!blockedReason} withArrow>
            <div>
              <ExportCsvButton<Row>
                data={rows}
                columns={columns}
                filename={filename}
                disabled={columnCount === 0 || Boolean(blockedReason)}
              />
            </div>
          </Tooltip>
        )}
      </Group>

      {mode === 'paged' && progress && (
        <Stack gap={4} mt="sm">
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {finished ? 'Export ready' : 'Fetching records…'}
            </Text>
            <Text size="xs" c="dimmed">
              {progress.loaded.toLocaleString()} of {progress.total.toLocaleString()} ({pct}%)
            </Text>
          </Group>
          <Progress
            value={pct}
            animated={busy}
            striped={busy}
            color={finished ? 'green' : 'blue'}
            aria-label="Export progress"
          />
        </Stack>
      )}
    </Paper>
  );
}
